import React from "react"
import { AppState } from "react-native"

import { useMMKVString } from "react-native-mmkv"

interface UserSpeechTiming {
  conversationId: string
  timestamp: number
  method: "button_tap" | "long_press_release" | "client_timeout"
}

interface TimingData {
  responseTime: number
  userStopMethod: string
}

interface ResponseTimingContextType {
  recordUserStopSpeaking: (
    conversationId: string,
    method: UserSpeechTiming["method"],
    timestamp?: number,
  ) => void
  consumeUserStopTiming: (conversationId: string) => TimingData | null
  clearTimingData: () => void
  cancelTimingOnError: (reason: string) => void
  currentLLMId: string | null
  setCurrentLLMId: (llmId: string) => void
}

const ResponseTimingContext = React.createContext<ResponseTimingContextType | null>(null)

/**
 * ResponseTimingProvider:
 *
 * Manages timing data between user stopping speech and sidekick starting speech.
 * This enables measurement of response latency while filtering out duplicate events
 * and only measuring between nearest neighbor events.
 */
export const ResponseTimingProvider = ({ children }: React.PropsWithChildren) => {
  const [currentLLMId, setCurrentLLMId] = useMMKVString("response-timing.currentLLMId")
  const [userStopTiming, setUserStopTiming] = React.useState<UserSpeechTiming | null>(null)

  const recordUserStopSpeaking = React.useCallback(
    (conversationId: string, method: UserSpeechTiming["method"], timestamp = Date.now()) => {
      console.log("[RESPONSE_TIMING] Recording user stop speaking", {
        conversationId,
        method,
        timestamp,
      })

      // Always overwrite previous timing data to handle duplicate user stops
      setUserStopTiming({
        conversationId,
        timestamp,
        method,
      })
    },
    [],
  )

  const consumeUserStopTiming = React.useCallback(
    (conversationId: string): TimingData | null => {
      if (!userStopTiming) {
        console.log("[RESPONSE_TIMING] No user stop timing data available")
        return null
      }

      if (userStopTiming.conversationId !== conversationId) {
        console.log("[RESPONSE_TIMING] Conversation ID mismatch, ignoring timing data", {
          stored: userStopTiming.conversationId,
          current: conversationId,
        })
        return null
      }

      const currentTime = Date.now()
      const responseTime = currentTime - userStopTiming.timestamp

      console.log("[RESPONSE_TIMING] Consuming user stop timing", {
        conversationId,
        responseTime,
        userStopMethod: userStopTiming.method,
      })

      const timingData: TimingData = {
        responseTime,
        userStopMethod: userStopTiming.method,
      }

      // Clear the timing data after consuming it
      setUserStopTiming(null)

      return timingData
    },
    [userStopTiming],
  )

  const clearTimingData = React.useCallback(() => {
    console.log("[RESPONSE_TIMING] Clearing timing data")
    setUserStopTiming(null)
  }, [])

  const cancelTimingOnError = React.useCallback(
    (reason: string) => {
      if (userStopTiming) {
        console.log("[RESPONSE_TIMING] Cancelling timing measurement due to error:", reason)
        setUserStopTiming(null)
      }
    },
    [userStopTiming],
  )

  // Cancel timing measurement when app goes to background
  React.useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState) => {
      if (nextAppState === "background" || nextAppState === "inactive") {
        if (userStopTiming) {
          console.log("[RESPONSE_TIMING] App backgrounded/inactive, cancelling timing measurement")
          setUserStopTiming(null)
        }
      }
    })

    return () => subscription.remove()
  }, [userStopTiming])

  const value = {
    recordUserStopSpeaking,
    consumeUserStopTiming,
    clearTimingData,
    cancelTimingOnError,
    currentLLMId: currentLLMId ?? null,
    setCurrentLLMId,
  }

  return <ResponseTimingContext.Provider value={value}>{children}</ResponseTimingContext.Provider>
}

export const useResponseTiming = () => {
  const context = React.useContext(ResponseTimingContext)
  if (!context) {
    throw new Error("useResponseTiming must be used within a ResponseTimingProvider")
  }
  return context
}
